import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Bar, BarChart, Cell, XAxis, YAxis } from 'recharts'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { useBreadcrumb } from '@/shared/context/BreadcrumbContext'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  FileSpreadsheet, Copy, ExternalLink,
  QrCode, ClipboardList, BarChart3, ChevronDown, ChevronUp,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Users, Phone, Send, Package, Info, CheckCircle,
} from 'lucide-react'
import { StatsCard } from '@/modules/link-tracker/components/StatsCard'
import { useCampania, useDescargarQR } from '../hooks/useCampanias'
import { useSolicitudes, solicitudesKeys, useActualizarSeguimiento } from '../hooks/useSolicitudes'
import { actualizarEstado as actualizarEstadoApi } from '../api/muestrasApi'
import { SolicitudesFiltros } from '../components/SolicitudesFiltros'
import { SolicitudesTabla } from '../components/SolicitudesTabla'
import { SeguimientoModal } from '../components/SeguimientoModal'
import { ExportarButtons } from '../components/ExportarButtons'
import type { FiltrosSolicitudes, ActualizarEstadoPayload, ActualizarSeguimientoPayload } from '../types'

export function CampaniaDetallePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaniaId = Number(id)
  const queryClient = useQueryClient()

  const [showInfo, setShowInfo] = useState(false)
  const [showAcciones, setShowAcciones] = useState(false)
  const [filtros, setFiltros] = useState<FiltrosSolicitudes>({ page: 1, page_size: 25 })
  const [selectedSolicitud, setSelectedSolicitud] = useState<number | null>(null)

  const { data: campania, isLoading } = useCampania(campaniaId)
  const { data: solicitudesData, isLoading: solicitudesLoading } = useSolicitudes(campaniaId, filtros)
  const actualizarSeguimiento = useActualizarSeguimiento(campaniaId)
  const qrMutation = useDescargarQR()
  const { setOverride, clearOverride } = useBreadcrumb()

  useDocumentTitle(campania?.titulo || 'Detalle Campaña')

  useEffect(() => {
    if (campania?.titulo) {
      setOverride(`/muestras-medicas/${id}`, campania.titulo)
    }
    return () => { clearOverride(`/muestras-medicas/${id}`) }
  }, [campania?.titulo, id, setOverride, clearOverride])

  const handleEstadoChange = async (payload: ActualizarEstadoPayload) => {
    try {
      await actualizarEstadoApi(payload)
      queryClient.invalidateQueries({ queryKey: solicitudesKeys.all })
      toast.success('Estado actualizado')
    } catch {
      toast.error('Error al actualizar estado')
    }
  }

  const handleSaveSeguimiento = async (payload: ActualizarSeguimientoPayload) => {
    await actualizarSeguimiento.mutateAsync(payload)
    setSelectedSolicitud(null)
  }

  const handleClearFiltros = () => {
    setFiltros({ page: 1, page_size: 25 })
  }

  const handleCopyUrl = async () => {
    if (!campania) return
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(campania.public_url)
      } else {
        throw new Error('clipboard not available')
      }
    } catch {
      setTimeout(() => {
        const input = document.createElement('input')
        input.value = campania.public_url
        input.style.position = 'fixed'
        input.style.left = '-9999px'
        document.body.appendChild(input)
        input.focus()
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
      }, 100)
    }
    toast.success('URL copiada al portapapeles')
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!campania) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Campaña no encontrada</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/muestras-medicas')}>
          Volver al listado
        </Button>
      </div>
    )
  }

  const tasaEntrega = campania.total_solicitudes > 0
    ? ((campania.total_entregados / campania.total_solicitudes) * 100).toFixed(1)
    : '0.0'
  const pendientes = campania.total_solicitudes - campania.total_entregados

  const distribucionData = [
    { nombre: 'Pendiente', valor: pendientes, fill: '#9ca3af' },
    { nombre: 'Contactados', valor: campania.total_contactados, fill: '#5eead4' },
    { nombre: 'Enviados', valor: campania.total_enviados, fill: '#14b8a6' },
    { nombre: 'Entregados', valor: campania.total_entregados, fill: '#0f766e' },
  ]

  const distribucionConfig = {
    valor: { label: 'Solicitudes' },
    Pendiente: { label: 'Pendiente', color: '#9ca3af' },
    Contactados: { label: 'Contactados', color: '#5eead4' },
    Enviados: { label: 'Enviados', color: '#14b8a6' },
    Entregados: { label: 'Entregados', color: '#0f766e' },
  } satisfies ChartConfig

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="h-6 w-6 text-black dark:text-white shrink-0" />
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2 whitespace-nowrap">
            {campania.titulo}
            <Badge className={campania.activa ? 'badge-gradient' : ''} variant={campania.activa ? 'default' : 'secondary'}>
              {campania.activa ? 'Activa' : 'Inactiva'}
            </Badge>
          </h1>
          {campania.descripcion && (
            <>
              <button
                onClick={() => setShowInfo(!showInfo)}
                className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                <Info className="h-4 w-4" />
              </button>
              {showInfo && (
                <span className="text-muted-foreground text-xs truncate max-w-[300px] animate-in fade-in duration-200" title={campania.descripcion}>
                  {campania.descripcion}
                </span>
              )}
            </>
          )}
        </div>
        <ExportarButtons campaniaId={campaniaId} />
      </div>

      {/* Resumen General */}
      <Card className="border border-gray-300 dark:border-gray-700 p-6">
        <h3 className="font-semibold text-foreground flex items-center mb-4">
          <BarChart3 className="h-4 w-4 text-teal-500 mr-2" />
          Resumen General
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-4">
          <StatsCard title="Total Solicitudes" value={campania.total_solicitudes} icon={Users} />
          <StatsCard title="Contactados" value={campania.total_contactados} icon={Phone} />
          <StatsCard title="Enviados" value={campania.total_enviados} icon={Send} />
          <StatsCard title="Entregados" value={campania.total_entregados} icon={Package} />
        </div>
        {campania.total_solicitudes > 0 && (
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground font-medium">Progreso de Entregas</span>
              <span className="font-semibold text-foreground">
                {campania.total_entregados} de {campania.total_solicitudes} ({tasaEntrega}%)
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-teal-500/10">
              <div
                className="h-2 rounded-full transition-all"
                style={{
                  background: 'linear-gradient(90deg, #14b8a6, #0f766e)',
                  width: `${(campania.total_entregados / campania.total_solicitudes) * 100}%`,
                }}
              />
            </div>
          </div>
        )}
      </Card>

      {/* Estadísticas de Muestras Médicas */}
      <Card className="border border-gray-300 dark:border-gray-700 p-4">
        <h3 className="font-semibold text-foreground flex items-center mb-4">
          <Package className="h-4 w-4 text-purple-500 mr-2" />
          Estadísticas de Muestras Médicas
          <Badge variant="secondary" className="ml-3 bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
            {campania.total_entregados} entregadas
          </Badge>
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Distribución por Estado - Gráfica de barras */}
          <div className="bg-white dark:bg-background rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h4 className="font-semibold text-foreground flex items-center mb-3">
              <BarChart3 className="h-4 w-4 text-teal-500 mr-2" />
              Distribución por Estado
            </h4>
            <ChartContainer config={distribucionConfig} className="h-[220px] w-full">
              <BarChart data={distribucionData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <XAxis
                  dataKey="nombre"
                  tickLine={false}
                  axisLine={false}
                  fontSize={12}
                  className="fill-muted-foreground"
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  fontSize={12}
                  allowDecimals={false}
                  className="fill-muted-foreground"
                />
                <ChartTooltip
                  cursor={false}
                  content={<ChartTooltipContent hideLabel />}
                />
                <Bar dataKey="valor" radius={[4, 4, 0, 0]}>
                  {distribucionData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
          </div>

          {/* Progreso por Etapa */}
          <div className="bg-white dark:bg-background rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <h4 className="font-semibold text-foreground flex items-center mb-3">
              <CheckCircle className="h-4 w-4 text-teal-500 mr-2" />
              Progreso por Etapa
            </h4>
            <div className="space-y-4">
              {/* Contactados */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Contactados</span>
                  <span className="text-muted-foreground font-semibold text-xs">
                    {campania.total_contactados} / {campania.total_solicitudes}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div
                    className="bg-teal-300 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${campania.total_solicitudes > 0 ? (campania.total_contactados / campania.total_solicitudes) * 100 : 0}%` }}
                  />
                </div>
              </div>
              {/* Enviados */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Enviados</span>
                  <span className="text-muted-foreground font-semibold text-xs">
                    {campania.total_enviados} / {campania.total_solicitudes}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div
                    className="bg-teal-500 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${campania.total_solicitudes > 0 ? (campania.total_enviados / campania.total_solicitudes) * 100 : 0}%` }}
                  />
                </div>
              </div>
              {/* Entregados */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Entregados</span>
                  <span className="text-muted-foreground font-semibold text-xs">
                    {campania.total_entregados} / {campania.total_solicitudes}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div
                    className="bg-teal-700 h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${campania.total_solicitudes > 0 ? (campania.total_entregados / campania.total_solicitudes) * 100 : 0}%` }}
                  />
                </div>
              </div>
              {/* Muestras Médicas */}
              <div className="pt-3 mt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground font-medium flex items-center">
                    <Package className="h-3.5 w-3.5 text-purple-500 mr-1" /> Muestras Médicas
                  </span>
                  <span className="font-semibold text-purple-600 dark:text-purple-400">
                    {campania.total_entregados} / {campania.total_solicitudes}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-purple-400 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${campania.total_solicitudes > 0 ? (campania.total_entregados / campania.total_solicitudes) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Acciones (collapsible) */}
      <Card className="border border-gray-300 dark:border-gray-700 p-4">
        <button
          onClick={() => setShowAcciones(!showAcciones)}
          className="w-full font-semibold text-foreground flex items-center justify-between cursor-pointer"
        >
          <span className="flex items-center">
            <ClipboardList className="h-4 w-4 text-teal-500 mr-2" />
            Acciones
          </span>
          {showAcciones ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </button>

        {showAcciones && (
          <div className="mt-4 space-y-3">
            <div>
              <p className="text-sm text-muted-foreground mb-1">URL Pública</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-muted p-2 rounded truncate">
                  {campania.public_url}
                </code>
                <Button variant="outline" size="sm" onClick={handleCopyUrl}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href={campania.public_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => qrMutation.mutate(campaniaId)}>
                <QrCode className="mr-2 h-4 w-4" /> Descargar QR
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/muestras-medicas/${campaniaId}/form-builder`)}
              >
                <ClipboardList className="mr-2 h-4 w-4" /> Form Builder
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/muestras-medicas/${campaniaId}/encuestas`)}
              >
                <BarChart3 className="mr-2 h-4 w-4" /> Encuestas
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Filtros */}
      <SolicitudesFiltros
        filtros={filtros}
        filtrosDisponibles={solicitudesData?.filtros_disponibles}
        onFiltrosChange={setFiltros}
        onClear={handleClearFiltros}
      />

      {/* Tabla de Solicitudes */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <div className="p-4">
          <h3 className="font-semibold text-foreground flex items-center mb-4">
            <FileSpreadsheet className="h-4 w-4 text-teal-500 mr-2" />
            Solicitudes Recibidas
            {solicitudesData && (
              <Badge variant="secondary" className="ml-2">
                {solicitudesData.count}
              </Badge>
            )}
          </h3>

          {solicitudesLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : solicitudesData && solicitudesData.results.length > 0 ? (
            <>
              <SolicitudesTabla
                solicitudes={solicitudesData.results}
                camposDinamicos={solicitudesData.campos_dinamicos}
                onEstadoChange={handleEstadoChange}
                onRowClick={(id) => setSelectedSolicitud(id)}
              />

              {/* Paginación */}
              <div className="flex items-center justify-between mt-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Mostrar</span>
                  <Select
                    value={(filtros.page_size || 25).toString()}
                    onValueChange={(value) => setFiltros((f) => ({ ...f, page: 1, page_size: parseInt(value) }))}
                  >
                    <SelectTrigger className="w-[80px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">por página</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setFiltros((f) => ({ ...f, page: 1 }))} disabled={(filtros.page || 1) === 1}>
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setFiltros((f) => ({ ...f, page: (f.page || 1) - 1 }))} disabled={(filtros.page || 1) === 1}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground min-w-[100px] text-center">
                    {filtros.page || 1} de {solicitudesData.total_pages}
                  </span>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setFiltros((f) => ({ ...f, page: (f.page || 1) + 1 }))} disabled={(filtros.page || 1) === solicitudesData.total_pages}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setFiltros((f) => ({ ...f, page: solicitudesData.total_pages }))} disabled={(filtros.page || 1) === solicitudesData.total_pages}>
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="py-12 text-center">
              <FileSpreadsheet className="mx-auto h-12 w-12 text-teal-500" />
              <h3 className="mt-4 text-lg font-semibold">No se encontraron solicitudes</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Ajusta los filtros o espera nuevas solicitudes
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Seguimiento Modal */}
      <SeguimientoModal
        solicitudId={selectedSolicitud}
        open={!!selectedSolicitud}
        onOpenChange={(open) => { if (!open) setSelectedSolicitud(null) }}
        onSave={handleSaveSeguimiento}
        isSaving={actualizarSeguimiento.isPending}
        campaniaTitulo={campania?.titulo}
        campaniaImagen={campania?.imagen_url}
      />
    </div>
  )
}
